import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { useAuth } from "@/_core/hooks/useAuth";

export function LoginContainer() {
    const [isRegistering, setIsRegistering] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { refresh } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        const endpoint = isRegistering ? "/api/register" : "/api/login";
        const payload = isRegistering ? { name, email, password } : { email, password };

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12000); // 12 second defensive timeout

        try {
            console.log(`[Frontend Auth] Submitting to ${endpoint}...`);
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                signal: controller.signal
            });

            console.log(`[Frontend Auth] Status received: `, res.status);
            
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("text/html")) {
               throw new Error("El servidor devolvió una respuesta inesperada (HTML). Comprueba tu conexión.");
            }

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || data.error || "Autenticación fallida");
            }

            if (isRegistering) {
                setIsRegistering(false);
                setError("Cuenta creada. Por favor, inicia sesión.");
            } else {
                console.log(`[Frontend Auth] Login OK, refreshing session...`);
                // Envolvemos refresh también en un Promise.race por si trpc/react-query se cuelga
                const refreshPromise = refresh();
                const timeoutPromise = new Promise((_, reject) => 
                    setTimeout(() => reject(new Error("Timeout al verificar estado de sesión (refresh)")), 8000)
                );
                await Promise.race([refreshPromise, timeoutPromise]);
                console.log(`[Frontend Auth] Session refresh verified.`);
            }
        } catch (err: any) {
            console.error(`[Frontend Auth] Request failed:`, err);
            if (err.name === 'AbortError' || err.message.includes('Timeout')) {
                setError("No se pudo iniciar sesión. La conexión tardó demasiado, inténtalo de nuevo.");
            } else {
                setError(err.message || "Error al conectar con el servidor.");
            }
        } finally {
            clearTimeout(timeoutId);
            console.log(`[Frontend Auth] Liberando candado de UI finalmente.`);
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50/50 p-4">
            <Card className="w-full max-w-md shadow-xl border-slate-200">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-bold text-center">
                        Iniciar sesión
                    </CardTitle>
                    <CardDescription className="text-center">
                        Ingresa tus credenciales para continuar
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Correo electrónico</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="juan@odsenergy.net"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Contraseña</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        {error && <p className="text-sm text-destructive text-center font-medium">{error}</p>}
                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? "Cargando..." : "Entrar"}
                        </Button>
                    </form>

                    <div className="grid gap-4">
                        <Button variant="outline" className="w-full" type="button" disabled>
                            Iniciar con Google (Próximamente)
                        </Button>
                    </div>
                </CardContent>
                <CardFooter>
                    <p className="w-full text-center text-xs text-slate-400">
                        Acceso restringido a personal autorizado.
                    </p>
                </CardFooter>
            </Card>
        </div>
    );
}
